/* eslint-disable replo/no-tailwind-literal-color */
import React, { useCallback } from "react";

import { useEditorStoreActions } from "@agent-ui/store/editorStore";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import InfiniteScroll from "@v2/components/InfiniteScroll";
import { Eye, EyeOff, History, X } from "lucide-react";

import { useGitHistory } from "./useGitHistory";
import { useGitPreview } from "./useGitPreview";
import { useGitRevert } from "./useGitRevert";
import { GitVersionCard, VersionCardSkeleton } from "./VersionCard";

const INITIAL_ITEMS_TO_LOAD = 20;

const VersionHistoryHeader = ({
  isInPreviewMode,
  onExitPreview,
  isLoadingPreview,
}: {
  isInPreviewMode: boolean;
  onExitPreview: () => void;
  isLoadingPreview: boolean;
}) => {
  const { setPanelView } = useEditorStoreActions();

  return (
    <div className="flex flex-col border-b-0.5 border-border">
      <div className="flex flex-row justify-between items-center p-4">
        <div className="header-h5">Version History</div>
        <Button
          icon
          onClick={() => setPanelView("chat")}
          variant="tertiary"
          size="sm"
        >
          <X />
        </Button>
      </div>

      {isInPreviewMode && (
        <div className="flex items-center gap-2 px-4 pb-3">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <Eye size={16} className="text-blue-600" />
            <span className="text-xs text-blue-800">
              Previewing older version
            </span>
          </div>
          <Button
            onClick={onExitPreview}
            variant="secondary"
            size="sm"
            loading={isLoadingPreview}
          >
            <EyeOff size={14} />
            Exit Preview
          </Button>
        </div>
      )}
    </div>
  );
};

const VersionHistoryContent = () => {
  const { commits, isLoading, hasMore, fetchMore } = useGitHistory();
  const { revert, isReverting } = useGitRevert();
  const {
    previewingSha,
    isLoadingPreview,
    isInPreviewMode,
    handlePreview,
    handleExitPreview,
  } = useGitPreview();

  const handleRevert = useCallback(
    async (sha: string) => {
      if (isInPreviewMode) {
        await handleExitPreview();
      }
      await revert(sha);
    },
    [isInPreviewMode, handleExitPreview, revert],
  );

  if (commits.length === 0 && !isLoading) {
    return (
      <>
        <VersionHistoryHeader
          isInPreviewMode={isInPreviewMode}
          onExitPreview={() => void handleExitPreview()}
          isLoadingPreview={isLoadingPreview}
        />
        <div className="flex justify-center items-center h-full">
          <Empty
            title="No versions yet"
            description="Version history will appear here as you make changes to your page."
            icon={<History />}
          />
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <VersionHistoryHeader
          isInPreviewMode={isInPreviewMode}
          onExitPreview={() => void handleExitPreview()}
          isLoadingPreview={isLoadingPreview}
        />
        <div className="flex flex-col gap-3 overflow-y-auto flex-1 p-4">
          {Array.from({ length: INITIAL_ITEMS_TO_LOAD }).map((_, index) => (
            <VersionCardSkeleton key={index} />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <VersionHistoryHeader
        isInPreviewMode={isInPreviewMode}
        onExitPreview={() => void handleExitPreview()}
        isLoadingPreview={isLoadingPreview}
      />
      <div
        className="flex flex-col gap-2 overflow-y-auto flex-1 p-4"
        id="versions-scroll-container"
      >
        <InfiniteScroll
          dataLength={commits.length}
          next={fetchMore}
          hasMore={hasMore}
          scrollableTarget="versions-scroll-container"
          loader={
            <div className="flex flex-col gap-3 overflow-y-auto flex-1 mt-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <VersionCardSkeleton key={index} />
              ))}
            </div>
          }
        >
          <div className="flex flex-col gap-3 w-full items-center">
            {commits.map((commit, index) => (
              <GitVersionCard
                key={commit.sha}
                commit={commit}
                isCurrentVersion={index === 0 && !isInPreviewMode}
                isPreviewing={previewingSha === commit.sha}
                onPreview={() => handlePreview(commit.sha)}
                onRevert={() => void handleRevert(commit.sha)}
                isReverting={isReverting}
                isLoadingPreview={
                  isLoadingPreview && previewingSha === commit.sha
                }
                className="w-full"
              />
            ))}
          </div>
        </InfiniteScroll>
      </div>
    </>
  );
};

function VersionHistory() {
  return (
    <div className="flex flex-col w-full h-full shadow-2xs border-0.5 border-border rounded-lg bg-white relative overflow-hidden">
      <VersionHistoryContent />
    </div>
  );
}

export default VersionHistory;
